/** The deputy's Serve Mode layout: load/save, apply night mode & text size, and the Customize screen. */
import { useEffect, useState } from 'react';
import { DEFAULT_SERVE_LAYOUT, isLockedPanel, normalizeServeLayout, SERVE_COLUMNS, SERVE_PANELS, type ServeArea, type ServeLayout } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { ErrorBox, useAction } from '../shared/ui.tsx';

const CACHE = 'civilhound.serveLayout';
const readCache = (): ServeLayout | null => {
  try {
    const v = localStorage.getItem(CACHE);
    return v ? normalizeServeLayout(JSON.parse(v)) : null;
  } catch {
    return null;
  }
};
const writeCache = (l: ServeLayout) => {
  try {
    localStorage.setItem(CACHE, JSON.stringify(l));
  } catch {
    /* private window */
  }
};

/** Effective layout: mine, else the agency default, else the built-in one. Cached for offline starts. */
export function useServeLayout(online: boolean) {
  const [layout, setLayout] = useState<ServeLayout>(() => readCache() ?? DEFAULT_SERVE_LAYOUT);
  const [agencyDefault, setAgencyDefault] = useState<ServeLayout | null>(null);
  const [custom, setCustom] = useState(false);
  const load = () =>
    api
      .get<{ serve: ServeLayout | null; agencyDefault: ServeLayout | null }>('/api/me/prefs')
      .then((r) => {
        const eff = normalizeServeLayout(r.serve ?? r.agencyDefault ?? DEFAULT_SERVE_LAYOUT);
        setAgencyDefault(r.agencyDefault);
        setCustom(!!r.serve);
        setLayout(eff);
        writeCache(eff);
      })
      .catch(() => undefined);
  useEffect(() => {
    if (online) load();
  }, [online]);
  const save = async (l: ServeLayout | null) => {
    await api.post('/api/me/prefs', { serve: l });
    await load();
  };
  const makeDefault = async (l: ServeLayout) => {
    await api.post('/api/admin/serve-layout', { layout: l });
    await load();
  };
  return { layout, setLayout, agencyDefault, custom, save, makeDefault };
}

/** Night mode and text size apply to the whole field app while it's open. */
export function useApplyLook(layout: ServeLayout) {
  useEffect(() => {
    const h = document.documentElement;
    h.classList.toggle('night', layout.theme === 'night');
    h.classList.remove('ts-large', 'ts-xlarge');
    if (layout.textSize !== 'normal') h.classList.add(`ts-${layout.textSize}`);
    return () => h.classList.remove('night', 'ts-large', 'ts-xlarge');
  }, [layout.theme, layout.textSize]);
}

const AREA_LABEL: Record<ServeArea, string> = { top: 'Top strip', left: 'Map side', right: 'Tiles' };
const labelOf = (area: ServeArea, id: string) => SERVE_PANELS[area].find((p) => p.id === id)?.label ?? id;
const move = <T,>(arr: T[], i: number, d: -1 | 1) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const out = [...arr];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
};

function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg cz-seg" role="radiogroup" aria-label={label}>
      {options.map(([v, l]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? 'active' : ''} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}

/** Customize Serve Mode. Changes show live behind the panel; Save keeps them, Cancel puts things back. */
export function CustomizeServe({ ctl, onClose }: { ctl: ReturnType<typeof useServeLayout>; onClose: () => void }) {
  const { can } = useAuth();
  const [start] = useState(ctl.layout);
  const l = ctl.layout;
  const set = (patch: Partial<ServeLayout>) => ctl.setLayout({ ...l, ...patch });
  const a = useAction();
  const [note, setNote] = useState<string | null>(null);
  const cancel = () => (ctl.setLayout(start), onClose());
  const colIds = SERVE_COLUMNS.map((c) => c.id);
  // Visible columns first (in their order), then the hidden ones.
  const colOrder = [...l.columns, ...colIds.filter((c) => !l.columns.includes(c))];
  return (
    <div className="modal-back cz-back" role="dialog" aria-label="Customize Serve Mode">
      <div className="modal wide cz">
        <div className="row between">
          <h2>Customize Serve Mode</h2>
          <button className="btn ghost" onClick={cancel} aria-label="Close">
            ✕
          </button>
        </div>
        <p className="small muted">Changes show behind this panel as you make them. Your layout follows you to any device you sign in on.</p>

        <section className="cz-sec">
          <h3>Look</h3>
          <div className="cz-row">
            <span>Screen</span>
            <Seg label="Screen" value={l.theme} onChange={(theme) => set({ theme })} options={[['day', '☀ Day'], ['night', '☾ Night']]} />
          </div>
          <div className="cz-row">
            <span>Text size</span>
            <Seg label="Text size" value={l.textSize} onChange={(textSize) => set({ textSize })} options={[['normal', 'Normal'], ['large', 'Large'], ['xlarge', 'Extra large']]} />
          </div>
          <div className="cz-row">
            <span>Map</span>
            <Seg label="Map side" value={l.mapSide} onChange={(mapSide) => set({ mapSide })} options={[['left', 'Left of the list'], ['right', 'Right of the list']]} />
          </div>
        </section>

        <section className="cz-sec">
          <h3>Panels</h3>
          <div className="cz-areas">
            {(['top', 'left', 'right'] as ServeArea[]).map((area) => (
              <div key={area} className="cz-area">
                <div className="small muted cz-area-h">{AREA_LABEL[area]}</div>
                <ul className="plain">
                  {l[area].map((p, i) => (
                    <li key={p.id} className={`cz-item${p.hidden ? ' off' : ''}`}>
                      <label className="grow">
                        <input
                          type="checkbox"
                          checked={!p.hidden}
                          disabled={isLockedPanel(p.id)}
                          aria-label={`Show ${labelOf(area, p.id)}`}
                          onChange={() => set({ [area]: l[area].map((x) => (x.id === p.id ? { ...x, hidden: !x.hidden } : x)) } as Partial<ServeLayout>)}
                        />{' '}
                        {labelOf(area, p.id)}
                        {isLockedPanel(p.id) && <span className="small muted"> · always shown (safety)</span>}
                      </label>
                      <button className="btn sm ghost" disabled={i === 0} aria-label={`Move ${labelOf(area, p.id)} up`} onClick={() => set({ [area]: move(l[area], i, -1) } as Partial<ServeLayout>)}>
                        ↑
                      </button>
                      <button className="btn sm ghost" disabled={i === l[area].length - 1} aria-label={`Move ${labelOf(area, p.id)} down`} onClick={() => set({ [area]: move(l[area], i, 1) } as Partial<ServeLayout>)}>
                        ↓
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="cz-sec">
          <h3>Paper list columns</h3>
          <p className="small muted">The paper (name, address, M-A-E-W circles) always shows.</p>
          <ul className="plain cz-cols">
            {colOrder.map((id) => {
              const on = l.columns.includes(id);
              const i = l.columns.indexOf(id);
              const label = SERVE_COLUMNS.find((c) => c.id === id)!.label;
              return (
                <li key={id} className={`cz-item${on ? '' : ' off'}`}>
                  <label className="grow">
                    <input type="checkbox" checked={on} aria-label={`Column ${label}`} onChange={() => set({ columns: on ? l.columns.filter((c) => c !== id) : [...l.columns, id] })} /> {label}
                  </label>
                  {on && (
                    <>
                      <button className="btn sm ghost" disabled={i === 0} aria-label={`Move column ${label} left`} onClick={() => set({ columns: move(l.columns, i, -1) })}>
                        ←
                      </button>
                      <button className="btn sm ghost" disabled={i === l.columns.length - 1} aria-label={`Move column ${label} right`} onClick={() => set({ columns: move(l.columns, i, 1) })}>
                        →
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          <label className="check small">
            <input type="checkbox" checked={l.showLegend} onChange={(e) => set({ showLegend: e.target.checked })} /> Show the “% = …” explanation under the list
          </label>
        </section>

        <ErrorBox error={a.error} />
        {note && <div className="small ok-text">{note}</div>}
        <div className="row gap wrap cz-actions">
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await ctl.save(l), onClose()))}>
            Save my layout
          </button>
          <button className="btn" disabled={a.busy} onClick={() => a.run(async () => (await ctl.save(null), onClose()))}>
            {ctl.agencyDefault ? 'Reset to agency default' : 'Reset to standard'}
          </button>
          {can('admin.rules' as any) && (
            <button
              className="btn ghost"
              disabled={a.busy}
              onClick={() =>
                window.confirm('Make this the default layout for every deputy who hasn’t customized their own?') && a.run(async () => (await ctl.makeDefault(l), setNote('Saved as the agency default.')))
              }
            >
              Make this the agency default
            </button>
          )}
          <button className="btn ghost" onClick={cancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
